import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Store d'authentification
 * Gère l'état de l'utilisateur, les quotas, et le modal de login
 */

export interface User {
  id: number
  email: string
  emailVerified: boolean
  quotaRemaining: number
  quotaResetAt?: string
  subscriptionStatus: 'free' | 'trial' | 'active' | 'cancelled'
  subscriptionExpiresAt?: string
  isSubscribed: boolean
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  quotaRemaining: number

  // UI State
  isLoginModalOpen: boolean
  loginStep: 'email' | 'otp' | 'success'
  pendingEmail: string | null
  authError: string | null

  // Actions
  setUser: (user: User | null) => void
  setQuota: (remaining: number) => void
  logout: () => void

  // Modal actions
  openLoginModal: () => void
  closeLoginModal: () => void
  setLoginStep: (step: 'email' | 'otp' | 'success') => void
  setPendingEmail: (email: string | null) => void
  setAuthError: (error: string | null) => void
  setLoading: (loading: boolean) => void

  // Reset
  resetAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      quotaRemaining: 3,

      // UI State
      isLoginModalOpen: false,
      loginStep: 'email',
      pendingEmail: null,
      authError: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          quotaRemaining: user?.quotaRemaining ?? 3,
        }),

      setQuota: (remaining) =>
        set({ quotaRemaining: remaining }),

      logout: () => {
        // Supprimer aussi du localStorage
        localStorage.removeItem('toxicology-auth')
        set({
          user: null,
          isAuthenticated: false,
          quotaRemaining: 3,
        })
      },

      // Modal actions
      openLoginModal: () =>
        set({
          isLoginModalOpen: true,
          loginStep: 'email',
          authError: null,
        }),

      closeLoginModal: () =>
        set({
          isLoginModalOpen: false,
          loginStep: 'email',
          pendingEmail: null,
          authError: null,
        }),

      setLoginStep: (step) =>
        set({ loginStep: step }),

      setPendingEmail: (email) =>
        set({ pendingEmail: email }),

      setAuthError: (error) =>
        set({ authError: error }),

      setLoading: (loading) =>
        set({ isLoading: loading }),

      resetAuth: () => {
        localStorage.removeItem('toxicology-auth')
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          quotaRemaining: 3,
          isLoginModalOpen: false,
          loginStep: 'email',
          pendingEmail: null,
          authError: null,
        })
      },
    }),
    {
      name: 'toxicology-auth',
      // Ne pas persister l'état du modal
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        quotaRemaining: state.quotaRemaining,
      }),
    }
  )
)

export default useAuthStore
